// The filter vocabulary Results and Responses share: the manifests kore's
// SearchBar renders on each page (built here from the survey and its
// placements, as EWT builds its own — the server has no facets endpoint yet),
// the mapping from kore's chip filters to the typed wire filters
// response_filters.rs accepts, and the URL dict a link to the other page
// carries so it opens on the same segment.

import {
    type BoolOp,
    buildFilters,
    type DurationOp,
    type EnumOp,
    type FilterDef,
    type FilterResponse,
    type FilterValue,
    type IdOp,
    type NullableIdOp,
    parseFilters,
    type TemporalOp,
    type TextOp,
} from "@pej/kore/components";
import type {
    ResponseSearchFilters,
    ResponseSearchQueryField,
    ResponseSortField,
    StepAnswerFilter,
} from "@/api/responses";
import type { Placement, StepRow, Survey } from "@/api/types";
import { metricStep, multiLabels, optionMeta } from "@/features/dashboard/stats-mapping";
import { acceptedOp } from "@/features/search/wire-op";
import { stepPrompt } from "@/features/surveys/steps/step-list";
import { choiceConfigSchema } from "@/features/surveys/survey-schema";
import { ALL_TIME_KEY } from "@/features/time/time-chip";

export const RESPONSE_SORT_FIELDS: readonly ResponseSortField[] = ["SubmittedAt", "Score"];
export const RESPONSE_QUERY_FIELDS: readonly ResponseSearchQueryField[] = ["Text"];
export const RESPONSE_DEFAULT_ORDER = { field: "SubmittedAt", order: "Desc" } as const;
export const RESPONSE_PAGE_SIZE_OPTIONS: readonly number[] = [25, 50, 100];

/** kore's chip filters as `useKoreSearch` hands them over. */
export type RawFilters = Record<string, { type: string } & Record<string, unknown>>;

const STEP_PARAM_PREFIX = "step_";
const TEMPORAL_OPS: readonly TemporalOp["type"][] = ["Eq", "Neq", "Lt", "Lte", "Gt", "Gte", "Between"];
const ENUM_OPS: readonly EnumOp<string>["type"][] = ["Eq", "Neq"];
// A step or placement id as the server parses it (`Uuid`): anything else in a
// hand-edited URL would be a 400 on every load, like an operator acceptedOp
// drops.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function stepFilterParam(stepId: string): string {
    return `${STEP_PARAM_PREFIX}${stepId}`;
}

export function sortedSteps(survey: Survey): StepRow[] {
    return [...survey.steps].sort((a, b) => a.position - b.position);
}

/** "1 · How was your meal?" — counted from 1 over the sorted steps. */
export function stepLabel(index: number, step: StepRow): string {
    return `${index + 1} · ${stepPrompt(step)}`;
}

/** score value -> option meta, off the scored step's config. Distinct from
 *  stats-mapping's `optionMeta` (keyed by option `key`): a `ResponseRow` only
 *  carries the numeric `score` it was submitted with, not which option key
 *  produced it, so the score badge has to look up by value instead.
 *
 *  Parsed with `choiceConfigSchema.safeParse` (same schema `survey-schema.ts`'s
 *  own `toStepsDefaults` uses) rather than a blind cast: `config` is
 *  `Record<string, unknown>` on the wire, so an unparseable step degrades to
 *  "no known score labels" — the badge then falls back to the bare numeric
 *  score — instead of throwing. Exported so this degrade behavior is
 *  directly unit-testable. */
export function optionByScore(step: StepRow | undefined): Map<number, { label: string; icon: string | null }> {
    const map = new Map<number, { label: string; icon: string | null }>();
    if (step?.kind !== "choice") return map;
    const parsed = choiceConfigSchema.safeParse(step.config);
    if (!parsed.success) return map;
    for (const option of parsed.data.options) {
        // An unscored choice step's options carry no `value` at all; skip
        // them rather than mis-keying the map under `undefined`.
        if (option.value == null) continue;
        const label = option.label && option.label.trim() !== "" ? option.label : option.key;
        map.set(option.value, { label, icon: option.icon ?? null });
    }
    return map;
}

/** A choice or multi step's options as filter values; a split step's two
 *  sets once each, first label wins. */
function stepOptionValues(step: StepRow): FilterValue[] {
    const labels =
        step.kind === "choice"
            ? new Map([...optionMeta(step)].map(([key, meta]) => [key, meta.label] as const))
            : multiLabels(step);
    return [...labels].map(([value, label]) => ({ value, label }));
}

/** The filters both pages offer (spec §3), in the bar's order. */
export function segmentFilterDefs(survey: Survey, placements: readonly Placement[]): FilterDef[] {
    const scored = metricStep(survey);
    const scoreValues: FilterValue[] = [...optionByScore(scored)].map(([value, meta]) => ({
        value: String(value),
        label: meta.label,
    }));
    const score: FilterDef[] =
        scoreValues.length > 0
            ? [{ type: "Enum", filterParam: "score", label: "Score", operators: ["Eq", "Neq"], values: scoreValues }]
            : [];
    // The score step has no step filter of its own: Score already covers it.
    const steps: FilterDef[] = sortedSteps(survey).flatMap((step, index): FilterDef[] =>
        (step.kind === "choice" || step.kind === "multi") && step.id !== scored?.id
            ? [
                  {
                      type: "Enum",
                      filterParam: stepFilterParam(step.id),
                      label: stepLabel(index, step),
                      operators: ["Eq", "Neq"],
                      values: stepOptionValues(step),
                  },
              ]
            : [],
    );
    return [
        { type: "Instant", filterParam: "submittedAt", label: "Submitted", operators: ["Gte", "Lt", "Between"] },
        { type: "Duration", filterParam: "submittedWithin", label: "Submitted within", operators: ["Last"] },
        {
            type: "Enum",
            filterParam: "placement",
            label: "Placement",
            operators: ["Eq"],
            values: placements.map((placement) => ({ value: placement.id, label: placement.name })),
        },
        { type: "Text", filterParam: "locationRef", label: "Location", operators: ["Contains"] },
        { type: "Bool", filterParam: "contextTrusted", label: "Verified", operators: ["Eq"] },
        { type: "Bool", filterParam: "completed", label: "Completed", operators: ["Eq"] },
        ...score,
        ...steps,
    ];
}

/** Results' segment bar: filters only, no search or sort. */
export function segmentManifest(survey: Survey, placements: readonly Placement[]): FilterResponse {
    return { filters: segmentFilterDefs(survey, placements) };
}

/** Responses' bar: the segment, plus what only a list can use. */
export function responsesManifest(survey: Survey, placements: readonly Placement[]): FilterResponse {
    return {
        query: { fields: [{ value: "Text", label: "Text answers" }] },
        sortBy: {
            fields: [
                { field: "SubmittedAt", label: "Submitted" },
                { field: "Score", label: "Score" },
            ],
            default: RESPONSE_DEFAULT_ORDER,
        },
        filters: [
            ...segmentFilterDefs(survey, placements),
            { type: "Bool", filterParam: "hasText", label: "Has text", operators: ["Eq"] },
            { type: "Id", filterParam: "respondentRef", label: "Respondent", operators: ["Eq"], nullable: true },
            // The sender's reference on an invite (an order id): a lookup,
            // like Respondent, not a slice.
            { type: "Text", filterParam: "reference", label: "Reference", operators: ["Contains"] },
        ],
    };
}

/** kore's chip filters → the wire. */
export function filtersForWire(raw: RawFilters | undefined): ResponseSearchFilters {
    const chips = raw ?? {};
    const wire: ResponseSearchFilters = {};
    const submittedAt = acceptedOp<TemporalOp>(chips.submittedAt, TEMPORAL_OPS);
    if (submittedAt) wire.submittedAt = submittedAt;
    const submittedWithin = acceptedOp<DurationOp>(chips.submittedWithin, ["Last"]);
    if (submittedWithin) wire.submittedWithin = submittedWithin;
    const score = acceptedOp<EnumOp<string>>(chips.score, ENUM_OPS);
    // Non-numbers would go out as JSON null, which the server refuses.
    const scores = (score?.values ?? []).map(Number).filter(Number.isFinite);
    if (score && scores.length > 0) wire.score = { type: score.type, values: scores };
    const hasText = acceptedOp<BoolOp>(chips.hasText, ["Eq"]);
    if (hasText) wire.hasText = hasText;
    const contextTrusted = acceptedOp<BoolOp>(chips.contextTrusted, ["Eq"]);
    if (contextTrusted) wire.contextTrusted = contextTrusted;
    const completed = acceptedOp<BoolOp>(chips.completed, ["Eq"]);
    if (completed) wire.completed = completed;
    const locationRef = acceptedOp<TextOp>(chips.locationRef, ["Contains"]);
    if (locationRef) wire.locationRef = locationRef;
    const respondentRef = acceptedOp<NullableIdOp>(chips.respondentRef, ["Eq"]);
    if (respondentRef) wire.respondentRef = respondentRef;
    const reference = acceptedOp<TextOp>(chips.reference, ["Contains"]);
    if (reference) wire.reference = reference;
    const answers = Object.entries(chips).flatMap(([key, op]): StepAnswerFilter[] => {
        if (!key.startsWith(STEP_PARAM_PREFIX)) return [];
        const stepId = key.slice(STEP_PARAM_PREFIX.length);
        const accepted = acceptedOp<EnumOp<string>>(op, ENUM_OPS);
        return accepted && UUID.test(stepId) ? [{ stepId, op: accepted }] : [];
    });
    if (answers.length > 0) wire.answers = answers;
    const placement = acceptedOp<IdOp>(chips.placement, ["Eq"]);
    // Ids the server can parse as UUIDs; anything else from a hand-edited URL is dropped.
    const placementIds = (placement?.values ?? []).filter((id) => UUID.test(id));
    if (placementIds.length > 0) wire.placement = { type: "Eq", values: placementIds };
    return wire;
}

const TIME_FILTERS = ["submittedAt", "submittedWithin"] as const;

/** Only the time filters: what the Export total counts over (spec §5). */
export function pickTime(filters: ResponseSearchFilters): ResponseSearchFilters {
    return {
        ...(filters.submittedAt ? { submittedAt: filters.submittedAt } : {}),
        ...(filters.submittedWithin ? { submittedWithin: filters.submittedWithin } : {}),
    };
}

/** Whether a chip is one of the time filters (Clear filters keeps it). */
export function isTimeFilter(filterParam: string): boolean {
    return (TIME_FILTERS as readonly string[]).includes(filterParam);
}

/** Whether anything but the time filters narrows the list. */
export function narrowsBeyondTime(filters: ResponseSearchFilters): boolean {
    return Object.keys(filters).some((key) => !isTimeFilter(key));
}

/** The Export button's label (spec §5): the count, and "of {total}" once
 *  something beyond the time filter narrows the list. */
export function exportLabel(count: number | undefined, total: number | undefined, narrowed: boolean): string {
    if (count === undefined) return "Export";
    if (!narrowed) return count === 1 ? "Export 1 response" : `Export ${count} responses`;
    return total === undefined ? `Export ${count}` : `Export ${count} of ${total}`;
}

/** The URL dict a link to the other page carries: the segment's chips in
 *  kore's grammar, plus the all-time marker. Has text and Respondent aren't
 *  segment filters, so they stay behind; the default chip stays implicit. */
export function segmentSearch(
    search: Record<string, unknown>,
    survey: Survey,
    placements: readonly Placement[],
): Record<string, string | string[]> {
    return {
        ...buildFilters(parseFilters(search, segmentFilterDefs(survey, placements))),
        ...(search[ALL_TIME_KEY] === "all" ? { [ALL_TIME_KEY]: "all" } : {}),
    };
}
